import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import React, {createRef, ReactNode, RefObject} from "react";
import ContentMode from "../../../ContentMode";
import {PortalGlobals} from "../../../Portal";
import PageListItem from "./PageListItem";
import {Slider} from "primereact/slider";
import DocumentSettings from "../../DocumentSettings";
import {InputText} from "primereact/inputtext";
import DomHelper from "../../../../shared/helper/DomHelper";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDialogType from "../../../dialog/PortalDialogType";
import Parameter from "../../../dialog/parameter/Parameter";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import PageSelection from "../../PageSelection";
import {TextSearchGlobals} from "./TextSearch";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {PREVIEW_SIZE} from "../../../startscreen/Preview";
import {RotateDialogDefinition} from "../../../dialog/components/page/RotateDialog";
import {DeletePageDialogDefinition} from "../../../dialog/components/page/DeletePageDialog";
import {PageExtractionDialogDefinition} from "../../../dialog/components/export/PageExtractionDialog";
import Exception from "../../../../shared/exception/Exception";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import MenuButton from "../../../../shared/menu/MenuButton";
import {
	Attachment,
	BaseToolbox,
	Information,
	Rectangle,
	Settings,
	SplitMode,
	ToolboxDeleteDelete,
	ToolboxRotateRotate
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxSplitSplit} from "../../../../shared/models/PortalToolboxSplitSplit";
import Immutable from "../../../../shared/immutable/Immutable";
import Translate from "../../../../shared/translation/Translate";
import RectSelection from "../selection/RectSelection";
import {SidebarFunctionGlobals} from "./SidebarFunctions";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import {isMobile} from "react-device-detect";
import {DragEventType, PortalDragEvent} from "../../../../shared/interface/PortalDragEvent";
import LoadingViewer from "../../../viewer/LoadingViewer";
import {ViewerMode} from "../../../viewer/ViewerMode";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

interface PageListProperties extends TextSearchGlobals, PortalGlobals, I18nGlobals, SidebarFunctionGlobals,
	DebugGlobals {
	portalDocument?: Immutable<PortalDocumentInterface>;
	navigateToDestination?: (dest: string | Array<any> | null) => Promise<void>;
	visible: boolean;
}

interface PageListStatus {
	currentPreviewZoomValue: number;
	previewZoomValue: number;
	showPageRange: boolean;
	pageSelection: string;
	pageSelectionActive: boolean;
	selectionHasError: boolean;
	loading: boolean;
	first: number;
	loadingOffset: number;
	loadedItemsFrom: number;
	loadedItemsTo: number;
	pageListHeight: number;
	numberOfPages: number;
	dragging: boolean;
}

class PageListDefinition extends PortalPureComponent<PageListProperties, PageListStatus> {
	private viewerElement: RefObject<HTMLDivElement>;
	private pageListElement: RefObject<HTMLDivElement>;
	private resizeObserver: ResizeObserver;

	/**
	 * Page list component constructor
	 */
	constructor(props: Readonly<PageListProperties>) {
		super(props);

		this.pageListElement = createRef<HTMLDivElement>();
		this.viewerElement = createRef<HTMLDivElement>();
		this.state = {
			currentPreviewZoomValue: 100,
			previewZoomValue: 100,
			showPageRange: false,
			pageSelection: "",
			pageSelectionActive: false,
			selectionHasError: false,
			loading: false,
			first: 0,
			loadingOffset: 0,
			loadedItemsFrom: 1,
			loadedItemsTo: 1,
			pageListHeight: 0,
			numberOfPages: 0,
			dragging: false
		};

		this.checkSelection = this.checkSelection.bind(this);
		this.selectAll = this.selectAll.bind(this);
		this.unselectAll = this.unselectAll.bind(this);
		this.togglePageRange = this.togglePageRange.bind(this);
		this.computeViewport = this.computeViewport.bind(this);
		this.deferredHandlePageRange = FunctionHelper.defer(this.deferredHandlePageRange.bind(this));
		this.deferedZoom = FunctionHelper.defer(this.deferedZoom.bind(this), 200);
		this.updateZoomValue = this.updateZoomValue.bind(this);
		this.onDragStart = this.onDragStart.bind(this);
		this.onDragEnd = this.onDragEnd.bind(this);

		this.resizeObserver = new ResizeObserver(this.computeViewport);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PageListProperties>, prevState: Readonly<PageListStatus>
	): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		await this.setNumberOfPages();

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let pageSelection: string = viewerSettings.pageSelection.toString(
			this.state.numberOfPages, viewerSettings.activeViewer.currentPage
		);

		if (this.state.pageSelection !== pageSelection) {
			this.deferredHandlePageRange(
				this.state.pageSelectionActive ? this.state.pageSelection : pageSelection
			);
		}

		if (this.props.portalDocument!.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument!.get().updated !== prevProps.portalDocument?.get().updated ||
			prevProps.visible !== this.props.visible) {
			if (FunctionHelper.exists(this.pageListElement.current)) {
				this.pageListElement.current!.scrollTop = 0;
			}

			this.computeViewport();
			return;
		}

		if (this.state.previewZoomValue !== viewerSettings.previewZoomValue) {
			this.updateZoomValue();
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('dragend', this.onDragEnd);

		await this.setNumberOfPages();
		this.updateZoomValue();
		this.computeViewport();

		if (FunctionHelper.exists(this.pageListElement.current)) {
			this.resizeObserver.observe(this.pageListElement.current!);
		}
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('dragend', this.onDragEnd);

		if (FunctionHelper.exists(this.pageListElement.current)) {
			this.resizeObserver.unobserve(this.pageListElement.current!);
		}
	}

	private checkSelection(options: Rectangle | null, event?: MouseEvent): void {
		if (!FunctionHelper.exists(options) || !FunctionHelper.exists(this.pageListElement.current) ||
			!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let pageListRect: DOMRect = this.pageListElement.current!.getBoundingClientRect();
		let pages: Array<HTMLElement> = DomHelper.findAll(this.pageListElement.current!, ".page-item");

		if (!event?.shiftKey && !event?.ctrlKey) {
			viewerSettings.pageSelection.clearPageSelection();
		}

		for (let page of pages) {
			let pageRect: DOMRect = page.getBoundingClientRect();
			let pageX: number = pageRect.x - pageListRect.x;
			let pageY: number = pageRect.y - pageListRect.y;
			let pageWidth: number = pageRect.width;
			let pageHeight: number = pageRect.height;

			let isSelected: boolean = (
					(pageX <= options!.x! + options!.width! &&
						(pageX >= options!.x! || options!.x! + options!.width! <= pageX + pageWidth)
					) ||
					(options!.x! <= pageX + pageWidth &&
						(pageX + pageWidth <= options!.x! + options!.width! || options!.x! >= pageX)
					)
				) &&
				(
					(options!.y! + options!.height! >= pageY &&
						(pageY >= options!.y! || options!.y! + options!.height! <= pageY + pageHeight)
					) ||
					(options!.y! <= pageY + pageHeight &&
						(pageY + pageHeight <= options!.y! + options!.height! || options!.y! >= pageY)
					)
				);

			let pageColumn: HTMLElement | null = DomHelper.find(page, ".column-page");
			if (isSelected && pageColumn !== null) {
				let pageNumber: number = parseInt(pageColumn.innerHTML);
				viewerSettings.pageSelection.selectPage(pageNumber, this.state.numberOfPages);
			}
		}

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * Defers a zoom request if another one is currently queued. required for slider inputs
	 */
	private setZoom(value: number): void {
		this.setState({
			currentPreviewZoomValue: value
		});

		this.deferedZoom(value);
	}

	/**
	 * Sets a custom preview zoom value in the context settings
	 */
	private deferedZoom(value: number) {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.previewZoomValue = value;

		this.props.context.documentManager.updateGlobalDocumentList();
		this.computeViewport();
	}

	private updateZoomValue() {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		this.setState({
			previewZoomValue: viewerSettings.previewZoomValue,
			currentPreviewZoomValue: viewerSettings.previewZoomValue
		});
	}

	/**
	 * selects all pages in the list
	 */
	private selectAll(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.pageSelection.selectAllPages(this.state.numberOfPages);

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * unselects all pages in the list
	 */
	private unselectAll(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		viewerSettings.pageSelection.clearPageSelection();

		this.props.context.documentManager.updateGlobalDocumentList();
	}

	/**
	 * Called on changing pagerange input
	 */
	private deferredHandlePageRange(value: string): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let tempValue: string = value;

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let hasError: boolean = false;

		try {
			if (viewerSettings.activeViewer.currentPage.toString() === value) {
				viewerSettings.pageSelection.clearPageSelection();
			} else {
				viewerSettings.pageSelection = PageSelection.fromString(tempValue, this.state.numberOfPages);
				tempValue = viewerSettings.pageSelection.toString(
					this.state.numberOfPages, viewerSettings.activeViewer.currentPage
				);
			}

			this.props.context.documentManager.updateGlobalDocumentList();
		} catch (error) {
			hasError = true;
		}

		this.setState({
			pageSelection: tempValue,
			selectionHasError: hasError
		});
	}

	/**
	 * toggles the page range input
	 */
	private togglePageRange(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		this.setState({
			showPageRange: !this.state.showPageRange,
			pageSelection: viewerSettings.pageSelection.toString(
				this.state.numberOfPages, viewerSettings.activeViewer.currentPage
			)
		});
	}

	private checkPasswords(): boolean {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return false;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError())
		) {
			return true;
		}

		return false;
	}

	private async deletePages(deleteParameter: Parameter<ToolboxDeleteDelete>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.deletePages(deleteParameter);
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		DialogDefault.setParameter(PortalDialogType.DELETE_PAGE, deleteParameter, [portalDocument]);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			delete: deleteParameter.data.toJson()
		});
		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				document: portalDocument,
				operation: operationData,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as Settings)
			});
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
				await this.deletePages(deleteParameter);
			}
			return;
		}

		let message: Message = new Message("success.toolbox.page.delete", MessageType.SUCCESS);
		message.context = {filename: '"' + portalDocument.getFileName() + '" '};
		this.props.context.getPage().addMessage(message);
	}

	private async extractPages(extractParameter: Parameter<PortalToolboxSplitSplit>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.extractPages(extractParameter);
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		DialogDefault.setParameter(PortalDialogType.EXTRACT_PAGE, extractParameter, [portalDocument]);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			split: extractParameter.data
		});
		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				document: portalDocument,
				operation: operationData,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as Settings)
			});
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
				await this.extractPages(extractParameter);
			}
			return;
		}

		let message: Message = new Message("success.toolbox.extract.page", MessageType.SUCCESS);
		message.context = {filename: '"' + portalDocument.getFileName() + '" '};
		this.props.context.getPage().addMessage(message);
	}

	private async rotatePages(rotateParameter: Parameter<ToolboxRotateRotate>): Promise<void> {
		if (this.checkPasswords()) {
			this.props.context.getPage().setState({
				showPasswordDialog: true,
				passwordRecallFunction: async () => {
					await this.rotatePages(rotateParameter);
				}
			});

			return;
		}

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		DialogDefault.setParameter(PortalDialogType.ROTATE, rotateParameter, [portalDocument]);

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			rotate: rotateParameter.data
		});
		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				document: portalDocument,
				operation: operationData,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as Settings)
			});
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
				await this.rotatePages(rotateParameter);
			}
			return;
		}

		let message: Message = new Message("success.toolbox.rotate", MessageType.SUCCESS);
		message.context = {filename: '"' + portalDocument.getFileName() + '" '};
		this.props.context.getPage().addMessage(message);
	}

	private computeViewport(): void {
		if (!FunctionHelper.exists(this.pageListElement.current) ||
			!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let previewSize: number = (viewerSettings.previewZoomValue / 100) * PREVIEW_SIZE;
		let pageItemHeight: number = previewSize + 44;
		let pageItemWidth: number = previewSize + 29;

		let pageListRect: DOMRect = this.pageListElement.current!.getBoundingClientRect();
		let pageListStyle: CSSStyleDeclaration = window.getComputedStyle(this.pageListElement.current!);
		let pageListHorizontalPadding: number =
			parseInt(pageListStyle.paddingLeft) + parseInt(pageListStyle.paddingRight);
		let pageListVerticalPadding: number =
			parseInt(pageListStyle.paddingTop) + parseInt(pageListStyle.paddingBottom);
		let pageListWidth: number = pageListRect.width - DomHelper.SCROLLBAR_SIZE - pageListHorizontalPadding;
		let pageListHeight: number = pageListRect.height - DomHelper.SCROLLBAR_SIZE - pageListVerticalPadding;

		let itemsPerRow: number = Math.floor(pageListWidth / pageItemWidth);
		let itemsInView: number = Math.round((pageListHeight / pageItemHeight) * itemsPerRow);
		let scrollOffset: number = this.pageListElement.current!.scrollTop;
		let preloaded: number = itemsPerRow;

		let listHeight: number = pageItemHeight * (this.state.numberOfPages / itemsPerRow);

		let loadedItemsFrom: number = (Math.floor(scrollOffset / pageItemHeight) * itemsPerRow) + 1;
		loadedItemsFrom -= preloaded;
		loadedItemsFrom = Math.max(loadedItemsFrom, 1);

		let loadedItemsTo: number = loadedItemsFrom + itemsInView + 1;
		loadedItemsTo += preloaded + (loadedItemsFrom - preloaded > 1 ? preloaded : 0);
		loadedItemsTo = Math.min(loadedItemsTo, this.state.numberOfPages);

		let loadingOffset: number = 0;
		if (loadedItemsFrom > 1) {
			loadingOffset = Math.floor((loadedItemsFrom - 1) / itemsPerRow) * pageItemHeight;
		}

		this.setState({
			loadingOffset: loadingOffset,
			loadedItemsFrom: loadedItemsFrom,
			loadedItemsTo: loadedItemsTo,
			pageListHeight: listHeight
		});
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let numberOfPages: number = 0;
		if (portalDocument.isPortfolioFile()) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
			);

			if (FunctionHelper.exists(attachment)) {
				numberOfPages = await portalDocument.getNumberOfPages(attachment);
			}
		} else {
			numberOfPages = await portalDocument.getNumberOfPages();
		}

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	private onDragStart(event: React.DragEvent, pageNumber: number): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		documentSettings.pageSelection.selectPage(pageNumber, this.state.numberOfPages);

		this.props.context.getPage().setState({
			dragEvent: new Immutable<PortalDragEvent>({
				type: DragEventType.PAGELIST,
				data: documentSettings.pageSelection.toString(this.state.numberOfPages, pageNumber)
			})
		});

		this.setState({
			dragging: true
		});
	}

	private onDragEnd(event: DragEvent): void {
		this.setState({
			dragging: false
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalInfo: Information = this.props.context.info;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();
		let isFolderSelected: boolean = FunctionHelper.exists(portalDocument.getFolderByPath(
			FunctionHelper.applyValue(documentSettings.portfolioSelection, "")
		));
		let isPortfolioFile: boolean = portalDocument.isPortfolioFile();

		let selectedAttachment: Attachment | undefined = portalDocument.getAttachment(
			!isFolderSelected ? FunctionHelper.applyValue(documentSettings.portfolioSelection, "") : ""
		);

		let pageListItems: Array<ReactNode> = [];
		for (let pageNumber: number = this.state.loadedItemsFrom;
			 pageNumber <= this.state.numberOfPages && pageNumber <= this.state.loadedItemsTo;
			 pageNumber++) {
			pageListItems.push(
				<PageListItem {...this.props} {...this.state} portalDocument={this.props.portalDocument!}
							  previewSize={(viewerSettings.previewZoomValue / 100) * PREVIEW_SIZE}
							  contentMode={ContentMode.STARTSCREEN} currentPage={pageNumber} key={pageNumber}
							  viewerElement={this.viewerElement}
							  active={viewerSettings.activeViewer.currentPage === pageNumber}
							  selected={viewerSettings.pageSelection.isPageSelected(pageNumber)}
							  attachmentKey={selectedAttachment?.objectKey}
							  onDragStartCapture={async (event: React.DragEvent) => {
								  if (isPortfolioFile) {
									  return;
								  }

								  let command: () => Promise<void> = async () => {
									  this.onDragStart(event, pageNumber);
								  }

								  if (DocumentSettings.checkActiveMouseFunction(this.props)) {
									  DocumentSettings.confirmResetMouseFunction(this.props, command);
									  return;
								  }

								  await command();
							  }}/>
			);
		}

		return (
			<div className="body" style={this.props.visible ? {} : {display: "none"}} data-help="global.sidebar.pages"
				 data-position="right">
				<div className="toolbar">
					<MenuButton {...this.props} disabled={this.state.numberOfPages === 0} onClick={this.selectAll}
								icon="icon icon-pages-select-all"
								tooltip={Translate.value("pages.select.all", {ns: "sidebar"})}/>
					<MenuButton {...this.props} disabled={this.state.numberOfPages === 0} onClick={this.unselectAll}
								icon="icon icon-pages-select-none"
								tooltip={Translate.value("pages.select.none", {ns: "sidebar"})}/>
					<MenuButton {...this.props} disabled={this.state.numberOfPages === 0} onClick={this.togglePageRange}
								icon="icon icon-pages-select-range"
								tooltip={Translate.value("pages.select.range", {ns: "sidebar"})}/>
					{
						(
							!this.props.documentChecks ||
							(
								(
									RotateDialogDefinition.isEnabled(portalInfo) ||
									DeletePageDialogDefinition.isEnabled(portalInfo) ||
									PageExtractionDialogDefinition.isEnabled(portalInfo)
								) && !isPortfolioFile
							)
						) &&
						<div className="separator"/>
					}
					{(!this.props.documentChecks || (RotateDialogDefinition.isEnabled(portalInfo) && !isPortfolioFile)) &&
						<MenuButton {...this.props}
									disabled={
										this.props.documentChecks &&
										(
											!RotateDialogDefinition.isActive(
												[portalDocument], portalInfo, this.props.signatureEditProfile
											) ||
											this.state.numberOfPages === 0 || this.state.selectionHasError
										)
									}
									icon="icon icon-rotate-90 small"
									tooltip={Translate.value("pages.rotate.90", {ns: "sidebar"})}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											let rotateParameter: Parameter<ToolboxRotateRotate> =
												DialogDefault.getParameter(
													PortalDialogType.ROTATE, [portalDocument]
												);
											rotateParameter.data.pages = this.state.pageSelection;
											rotateParameter.data.degrees = 90;
											await this.rotatePages(rotateParameter);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
					}
					{(!this.props.documentChecks || (RotateDialogDefinition.isEnabled(portalInfo) && !isPortfolioFile)) &&
						<MenuButton {...this.props}
									disabled={
										this.props.documentChecks &&
										(
											!RotateDialogDefinition.isActive(
												[portalDocument], portalInfo, this.props.signatureEditProfile
											) ||
											this.state.numberOfPages === 0 || this.state.selectionHasError
										)
									}
									icon="icon icon-rotate-270 small"
									tooltip={Translate.value("pages.rotate.270", {ns: "sidebar"})}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											let rotateParameter: Parameter<ToolboxRotateRotate> =
												DialogDefault.getParameter(
													PortalDialogType.ROTATE, [portalDocument]
												);
											rotateParameter.data.pages = this.state.pageSelection;
											rotateParameter.data.degrees = 270;
											await this.rotatePages(rotateParameter);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
					}
					{(!this.props.documentChecks || (DeletePageDialogDefinition.isEnabled(portalInfo) && !isPortfolioFile)) &&
						<MenuButton {...this.props}
									disabled={
										this.props.documentChecks &&
										(
											!DeletePageDialogDefinition.isActive(
												[portalDocument], portalInfo, this.props.signatureEditProfile
											) ||
											this.state.numberOfPages === 0 || this.state.selectionHasError
										)
									}
									icon="icon icon-delete small"
									tooltip={Translate.value("pages.delete", {ns: "sidebar"})}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											let deletePageParameter: Parameter<ToolboxDeleteDelete> =
												DialogDefault.getParameter(
													PortalDialogType.DELETE_PAGE, [portalDocument]
												);
											deletePageParameter.data.pages = this.state.pageSelection;
											await this.deletePages(deletePageParameter);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
					}
					{(!this.props.documentChecks || (PageExtractionDialogDefinition.isEnabled(portalInfo) && !isPortfolioFile)) &&
						<MenuButton {...this.props}
									disabled={
										this.props.documentChecks &&
										(
											!PageExtractionDialogDefinition.isActive(
												[portalDocument], portalInfo, this.props.signatureEditProfile
											) ||
											this.state.numberOfPages === 0 || this.state.selectionHasError
										)
									}
									icon="icon icon-export small"
									tooltip={Translate.value("pages.export", {ns: "sidebar"})}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											let extractPageParameter: Parameter<PortalToolboxSplitSplit> =
												DialogDefault.getParameter(
													PortalDialogType.EXTRACT_PAGE, [portalDocument]
												);
											extractPageParameter.data.mode = SplitMode.Single;
											extractPageParameter.data.pages = this.state.pageSelection;
											await this.extractPages(extractPageParameter);
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
					}
				</div>
				{this.state.showPageRange &&
					<div className="toolbar stretched">
						<div className="p-col">
							<InputText className={"page-search" + (this.state.selectionHasError ? " p-invalid" : "")}
									   tooltip={
										   this.state.selectionHasError ?
											   Translate.value("errors.fields.pagerange", {ns: "message"}) : ""
									   }
									   type="search" value={this.state.pageSelection}
									   onChange={(event: any) => {
										   let value: string = event.target.value;
										   let hasError: boolean = false;

										   try {
											   PageSelection.fromString(value, this.state.numberOfPages);
										   } catch (error) {
											   hasError = true;
										   }

										   this.setState({
											   pageSelection: value,
											   selectionHasError: hasError
										   });
									   }}
									   onFocus={() => {
										   this.setState({
											   pageSelectionActive: true
										   });
									   }}
									   onBlur={() => {
										   this.setState({
											   pageSelectionActive: false
										   });
									   }}
									   placeholder={Translate.value(
										   "pages.select.placeholder", {ns: "sidebar"}
									   )}/>
						</div>
					</div>
				}
				<div className="selection-container">
					{portalDocument.isLocked() ?
						<LoadingViewer {...this.props} viewerMode={ViewerMode.PREVIEW}/> :
						<>
							<div className={"page-list" + (this.state.dragging ? " dragging" : "")}
								 ref={this.pageListElement}
								 onScroll={this.computeViewport}>
								{pageListItems.length === 0 &&
									<p className="no-results">
										<Translate {...this.props} data="pages.empty" namespace="screen"/>
									</p>
								}
								<div className="loading-spacer" style={this.state.pageListHeight > 0 ?
									{height: this.state.pageListHeight + LengthUnit.PIXEL} : undefined
								}/>
								<div className="page-container"
									 style={{top: this.state.loadingOffset + LengthUnit.PIXEL}}>
									{pageListItems}
								</div>
							</div>
							{FunctionHelper.exists(this.pageListElement.current) && !isMobile &&
								<RectSelection {...this.props} moveCallback={this.checkSelection}
											   getParent={() => {
												   return this.pageListElement.current;
											   }}
											   startCallback={(values, event) => {
												   if (event?.shiftKey || event?.ctrlKey) {
													   return;
												   }

												   this.unselectAll();
											   }}/>
							}
						</>
					}
				</div>
				<div className="toolbar inverted">
					<MenuButton {...this.props} icon="icon icon-pages-zoom"
								disabled={this.state.numberOfPages === 0}
								tooltip={Translate.value("pages.zoom.label", {ns: "sidebar"})}
								menuItems={
									[
										{
											label: Translate.value("pages.zoom.small", {ns: "sidebar"}),
											command: () => {
												this.setZoom(50);
											}
										},
										{
											label: Translate.value("pages.zoom.medium", {ns: "sidebar"}),
											command: () => {
												this.setZoom(125);
											}
										},
										{
											label: Translate.value("pages.zoom.large", {ns: "sidebar"}),
											command: () => {
												this.setZoom(200);
											}
										}
									]
								}/>
					<div className="page-slider">
						<Slider value={this.state.currentPreviewZoomValue} disabled={this.state.numberOfPages === 0}
								max={200} min={50}
								onChange={
									(e) => this.setZoom(e.value as number)
								}/>
					</div>
				</div>
			</div>
		);
	}
}

const PageList = React.memo(PageListDefinition,
	(prevProps: Readonly<PageListProperties>, nextProps: Readonly<PageListProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.dragEvent) && !nextProps.dragEvent!.equals(prevProps.dragEvent)) {
			return false;
		}

		if (nextProps.visible !== prevProps.visible) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.showAnnotationLineFunction !== prevProps.showAnnotationLineFunction) {
			return false;
		}

		if (nextProps.showAnnotationCircleFunction !== prevProps.showAnnotationCircleFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquareFunction !== prevProps.showAnnotationSquareFunction) {
			return false;
		}

		if (nextProps.showAnnotationMarkFunction !== prevProps.showAnnotationMarkFunction) {
			return false;
		}

		if (nextProps.showAnnotationUnderlineFunction !== prevProps.showAnnotationUnderlineFunction) {
			return false;
		}

		if (nextProps.showAnnotationSquigglyFunction !== prevProps.showAnnotationSquigglyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStrikeFunction !== prevProps.showAnnotationStrikeFunction) {
			return false;
		}

		if (nextProps.showAnnotationNoteFunction !== prevProps.showAnnotationNoteFunction) {
			return false;
		}

		if (nextProps.showRedactAreaFunction !== prevProps.showRedactAreaFunction) {
			return false;
		}

		if (nextProps.showRedactTextFunction !== prevProps.showRedactTextFunction) {
			return false;
		}

		if (nextProps.showAnnotationReplyFunction !== prevProps.showAnnotationReplyFunction) {
			return false;
		}

		if (nextProps.showAnnotationStampFunction !== prevProps.showAnnotationStampFunction) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		return true;
	}
);

export default PageList;